import { SITE } from "@/config/site";

// Central per-route SEO metadata and OG image URLs.
// Every absolute URL uses the single production canonical domain from SITE.
const D = SITE.domain;
export const OG_DEFAULT = `${D}/og-default.png`;
export const ogPage = (slug) => `${D}/og/page/${slug}.png`;
export const ogService = (slug) => `${D}/og/service/${slug}.png`;
export const ogPost = (slug) => `${D}/og/post/${slug}.png`;

const siteDesc =
  "A2Z Rankings is an international technology, software development and digital growth agency serving Australia, India, the USA, UK and Canada.";

const org = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: D,
  logo: `${D}/favicon.svg`,
  email: SITE.email,
  description: siteDesc,
  areaServed: SITE.markets,
  sameAs: SITE.socials.filter((s) => s.href && s.href !== "#").map((s) => s.href),
};

const webSite = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE.name,
  url: D,
  description: siteDesc,
  publisher: { "@type": "Organization", name: SITE.name },
  inLanguage: "en",
};

// Reusable WebPage entity (about/work/services/etc.) — no fabricated claims.
// Reusable WebPage entity (about/work/services/etc.) — no fabricated claims.
const webPage = (name, path, description) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name,
  url: `${D}${path}`,
  description,
  isPartOf: { "@type": "WebSite", name: SITE.name, url: D },
  about: { "@type": "Organization", name: SITE.name, url: D },
  inLanguage: "en",
});

const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${D}${it.path}`,
  })),
});

export const ROUTE_SEO = {
  home: {
    title: null, // Seo component appends the brand suffix
    description:
      "A2Z Rankings builds custom software, web platforms, SaaS, mobile apps and AI automation — then grows them with SEO and digital marketing. Serving Australia and international markets.",
    canonicalPath: "/",
    ogImage: ogPage("home"),
    ogType: "website",
    // The static index.html already emits the Organization node; emit WebSite + BreadcrumbList here.
    jsonLd: [webSite, breadcrumb([{ name: "Home", path: "/" }])],
  },
  services: {
    title: "Services",
    description:
      "Software and web development, mobile apps, AI and automation, SEO and digital growth, and UI/UX design from A2Z Rankings.",
    canonicalPath: "/services",
    ogImage: ogPage("services"),
    jsonLd: [
      webPage("Services", "/services", "Software and web development, mobile apps, AI and automation, SEO and digital growth, and UI/UX design."),
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
      ]),
    ],
  },
  about: {
    title: "About Us",
    description:
      "A2Z Rankings is an international technology and digital growth agency focused on Australian businesses. Learn how we build and grow software.",
    canonicalPath: "/about",
    ogImage: ogPage("about"),
    jsonLd: [
      webPage("About A2Z Rankings", "/about", "About A2Z Rankings — an international technology and digital growth agency."),
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ]),
    ],
  },
  work: {
    title: "Our Work",
    description:
      "Concept projects and case studies from A2Z Rankings — software platforms, mobile apps, AI automation and digital growth work.",
    canonicalPath: "/work",
    ogImage: ogPage("work"),
    jsonLd: [
      webPage("Our Work", "/work", "Concept projects and case studies from A2Z Rankings."),
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Work", path: "/work" },
      ]),
    ],
  },
  resources: {
    title: "Resources & Insights",
    description:
      "Guides and honest thinking on software delivery, AI adoption and digital growth from the A2Z Rankings team.",
    canonicalPath: "/resources",
    ogImage: ogPage("resources"),
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Resources", path: "/resources" },
      ]),
    ],
  },
  contact: {
    title: "Contact Us",
    description:
      "Start a project conversation with A2Z Rankings. Tell us about your software, app, AI or growth goals and we will get back to you.",
    canonicalPath: "/contact",
    ogImage: ogPage("contact"),
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact A2Z Rankings",
        url: `${D}/contact`,
        description:
          "Start a project conversation with A2Z Rankings about software, app, AI or digital growth.",
        isPartOf: { "@type": "WebSite", name: SITE.name, url: D },
        about: { "@type": "Organization", name: SITE.name, url: D, email: SITE.email },
      },
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ]),
    ],
  },
  privacy: {
    title: "Privacy Policy",
    description:
      "How A2Z Rankings collects, uses and protects personal information when you visit our website or work with us.",
    canonicalPath: "/privacy-policy",
    ogImage: ogPage("privacy-policy"),
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Privacy Policy", path: "/privacy-policy" },
      ]),
    ],
  },
  terms: {
    title: "Terms & Conditions",
    description:
      "The terms and conditions that govern your use of the A2Z Rankings website and our services.",
    canonicalPath: "/terms-and-conditions",
    ogImage: ogPage("terms-and-conditions"),
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Terms & Conditions", path: "/terms-and-conditions" },
      ]),
    ],
  },
  refunds: {
    title: "Refunds & Cancellations",
    description:
      "A2Z Rankings' refund and cancellation policy for project work, retainers and digital services.",
    canonicalPath: "/refund-and-cancellation-policy",
    ogImage: ogPage("refund-and-cancellation-policy"),
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Refunds & Cancellations", path: "/refund-and-cancellation-policy" },
      ]),
    ],
  },
  nda: {
    title: "Mutual Non-Disclosure Agreement",
    description:
      "A2Z Rankings' mutual non-disclosure agreement template for confidential project discussions.",
    canonicalPath: "/non-disclosure-agreement",
    ogImage: ogPage("non-disclosure-agreement"),
    jsonLd: [
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Mutual NDA", path: "/non-disclosure-agreement" },
      ]),
    ],
  },
  seoPricing: {
    title: "SEO Pricing & Packages",
    description:
      "Transparent SEO packages from A2Z Rankings — Basic, Silver, Gold and Premium. Keyword research, technical SEO, content and link acquisition. Request a quote.",
    canonicalPath: "/seo-pricing/",
    ogImage: ogPage("seo-pricing"),
    jsonLd: [
      webPage("SEO Pricing & Packages", "/seo-pricing/", "Standard SEO packages from A2Z Rankings."),
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "SEO Pricing", path: "/seo-pricing/" },
      ]),
    ],
  },
  eliteSeoPackages: {
    title: "Elite SEO Packages",
    description:
      "Enterprise SEO packages from A2Z Rankings — Professional, Corporate and Enterprise. Advanced technical SEO, international, ecommerce, AI visibility (GEO/AEO) and authority development.",
    canonicalPath: "/elite-seo-packages/",
    ogImage: ogPage("elite-seo-packages"),
    jsonLd: [
      webPage("Elite SEO Packages", "/elite-seo-packages/", "Enterprise SEO packages from A2Z Rankings."),
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Elite SEO Packages", path: "/elite-seo-packages/" },
      ]),
    ],
  },
  notFound: {
    title: "Page Not Found",
    description: "The page you are looking for does not exist or has been moved.",
    canonicalPath: undefined,
    noindex: true,
  },
  serviceNotFound: {
    title: "Service Not Found",
    description: "The service you are looking for does not exist or is not published.",
    canonicalPath: undefined,
    noindex: true,
  },
  articleNotFound: {
    title: "Article Not Found",
    description: "This article does not exist or has been unpublished.",
    canonicalPath: undefined,
    noindex: true,
  },
};
